import type { Locale } from "@/i18n";
import type { Category } from "@/lib/types";

import { enDictionary } from "./dictionaries/en";
import { zhCNDictionary } from "./dictionaries/zh-CN";
import { zhTWDictionary } from "./dictionaries/zh-TW";

/**
 * DICTIONARY-BASED CATEGORIZATION
 *
 * Flow:
 * 1. Quantity Stripping - Remove quantity/container prefixes
 * 2. Plural Removal - Convert to singular form (English only)
 * 3. Exact Lookup - Check merged multilingual dictionary (lowercase + trim)
 * 4. Fallback - Return "other" if not found
 *
 * Language-agnostic approach:
 * - Users can type in any supported language (en, zh-CN, zh-TW, etc.)
 * - No locale detection needed - all dictionaries merged into one
 * - Scales to 10+ languages easily - just add more dictionaries
 *
 * Dictionaries contain only singular base forms without quantities.
 * All variations are handled algorithmically.
 */

// Merge all dictionaries into a single language-agnostic lookup
// This allows users to type in any language without restriction
const MERGED_DICTIONARY: Record<string, Category> = {
  ...enDictionary,
  ...zhCNDictionary,
  ...zhTWDictionary,
  // Future: Add more languages here (ja, ko, es, fr, de, etc.)
};

// Keep locale-specific dictionaries for backwards compatibility and testing
const DICTIONARIES: Record<string, Record<string, Category>> = {
  en: enDictionary,
  "zh-CN": zhCNDictionary,
  "zh-TW": zhTWDictionary,
};

/**
 * Detect if ingredient is in a shelf-stable container
 * These containers typically indicate the item is in the canned/dry goods section (other)
 * rather than fresh sections (produce/meat/seafood/dairy)
 *
 * Handles various input patterns:
 * - "can of tuna" / "can tuna" (with or without "of")
 * - "5 cans of corn" / "5 cans corn" (with quantity)
 * - "a can of beans" / "a can beans" (with article)
 * - "1 lb can of tomatoes" (with weight)
 *
 * @param text - Raw ingredient text
 * @returns Object with { hasShelfStableContainer: boolean, baseItem: string }
 */
function detectShelfStableContainer(text: string): {
  hasShelfStableContainer: boolean;
  baseItem: string;
} {
  const normalized = text.toLowerCase().trim();

  // Shelf-stable containers: can, jar, box
  // Pattern breakdown:
  // ^(?:...)?  - optional prefix (number, article, weight)
  // (can|cans|jar|jars|box|boxes) - the container word
  // \s+(?:of\s+)?  - space + optional "of"
  // (.+)$ - the actual food item
  const shelfStablePattern =
    /^(?:(?:\d+(?:\/\d+)?|a|an)\s*(?:g|kg|lb|lbs|oz|ounce|ounces|pound|pounds|gram|grams|kilogram|kilograms)?\s+)?(can|cans|jar|jars|box|boxes)\s+(?:of\s+)?(.+)$/i;

  const match = normalized.match(shelfStablePattern);
  if (match) {
    const baseItem = match[2].trim();
    return { hasShelfStableContainer: true, baseItem };
  }

  return { hasShelfStableContainer: false, baseItem: text };
}

/**
 * Remove quantity prefixes from ingredient text
 *
 * Handles:
 * - English: "2 lbs chicken", "500g beef", "1 bottle milk"
 * - Chinese: "一斤鸡肉", "500克牛肉", "2瓶牛奶"
 * - Containers: "bag of lettuce", "2 cans of tuna", "1 lb bag of carrots"
 *
 * Note: Preserves percentages like "2% milk", "1% yogurt"
 */
function removeQuantityPrefix(text: string): string {
  let normalized = text;

  // Skip if text contains percentage (like "2% milk")
  if (/%/.test(text)) {
    return normalized;
  }

  // Container patterns: "bag of lettuce", "2 cans of tuna", "1 lb package of chicken"
  // This handles: [number] [unit] [container] of [item]
  // Examples:
  // - "bag of spinach" → "spinach"
  // - "2 bags of lettuce" → "lettuce"
  // - "1 lb bag of carrots" → "carrots"
  // - "16 oz package of bacon" → "bacon"
  normalized = normalized.replace(
    /^(?:\d+(?:\.\d+)?\s*(?:g|kg|lb|lbs|oz|ounce|ounces|pound|pounds|gram|grams|kilogram|kilograms)?\s*)?(bag|bags|can|cans|jar|jars|bottle|bottles|box|boxes|pack|packs|package|packages|carton|cartons|container|containers|head|heads|bunch|bunches|loaf|loaves|stalk|stalks|rib|ribs|sprig|sprigs|handful|handfuls)\s+of\s+/gi,
    ""
  );

  // English quantity patterns
  // Remove leading numbers with optional units
  normalized = normalized.replace(
    /^\d+(?:\.\d+)?(?:\/\d+)?\s*(?:g|kg|lb|lbs|oz|ounce|ounces|pound|pounds|gram|grams|kilogram|kilograms|milliliter|milliliters|liter|liters|ml|l|piece|pieces|slice|slices|handful|handfuls|can|cans|jar|jars|bottle|bottles|box|boxes|bag|bags|pack|packs|package|packages|carton|cartons|container|containers|cup|cups|pint|pints|quart|quarts|gallon|gallons|tablespoon|tablespoons|tbsp|teaspoon|teaspoons|tsp)\s+/gi,
    ""
  );

  // Remove standalone numbers at the start (including fractions like "1/2")
  normalized = normalized.replace(/^\d+(?:\/\d+)?(?:\.\d+)?\s+/, "");

  // Chinese quantity patterns
  // First, handle mixed format: Arabic numerals + units (Latin or Chinese)
  // Examples: "500g牛肉", "500克牛肉", "250ml牛奶", "1升牛奶"
  normalized = normalized.replace(
    /^\d+(?:\.\d+)?(g|kg|ml|l|克|斤|两|兩|磅|公斤|千克|台斤|升|毫升)/gi,
    ""
  );

  // Chinese numerals: 一二三四五六七八九十百千万萬 + 半 (half) + 两兩 (two/a couple)
  // Includes both Simplified (两万) and Traditional (兩萬) characters
  const chineseNumerals = "[一二三四五六七八九十百千万萬半两兩0-9]+";

  // Handle Chinese numerals + weight/volume units
  // Examples: "一斤苹果" -> "苹果", "两斤猪肉" -> "猪肉", "半斤虾" -> "虾"
  //           "兩斤豬肉" -> "豬肉", "半斤蝦" -> "蝦" (Traditional)
  //           "一台斤芭樂" -> "芭樂" (Taiwanese catty)
  normalized = normalized.replace(
    new RegExp(`^${chineseNumerals}(台斤|克|斤|两|兩|磅|公斤|千克|升|毫升|ml|l|kg|g)`, "gi"),
    ""
  );

  // Chinese measure words (量词) - common ones for food
  // Includes both Simplified and Traditional variants: 个個 只隻 条條 头頭 颗顆 etc.
  const chineseMeasures =
    "[个個只隻条條根把片块塊包袋瓶罐盒听聽桶箱串颗顆粒枚支张張份碗盘盤碟杯勺头頭台]";

  // Remove Chinese numerals + ONE measure word (not multiple)
  // Examples: "2颗包菜" -> "包菜", "三个鸡蛋" -> "鸡蛋", "2头蒜" -> "蒜", "半个西瓜" -> "西瓜"
  //           "2個雞腿" -> "雞腿" (Traditional)
  // Note: This only matches when followed by a measure word to avoid removing
  // variety names like "8424西瓜" (8424 is a watermelon variety, not a quantity)
  normalized = normalized.replace(new RegExp(`^${chineseNumerals}${chineseMeasures}`, "g"), "");

  return normalized.trim();
}

/**
 * Convert plural forms to singular (English only)
 */
export function singularize(word: string): string {
  // Don't singularize words that are already singular or special cases
  const exceptions = [
    "rice",
    "pasta",
    "butter",
    "milk",
    "water",
    "juice",
    "bread",
    "meat",
    "fish",
    "seafood",
    "beef",
    "pork",
    "lamb",
    "flour",
    "sugar",
    "salt",
    "pepper",
    "oil",
    "vinegar",
    "grass",
    "bass",
    "glass",
    "brass",
  ];

  // Exact match exceptions (words that should NOT be singularized)
  const exactExceptions = ["lettuce", "spinach", "cheese"];

  if (exceptions.some((ex) => word === ex || word.includes(ex))) {
    return word;
  }

  if (exactExceptions.includes(word)) {
    return word;
  }

  // Common irregular plurals
  const irregulars: Record<string, string> = {
    tomatoes: "tomato",
    potatoes: "potato",
    mangoes: "mango",
    avocados: "avocado",
    lettuces: "lettuce",
    cheeses: "cheese",
    watermelons: "watermelon",
    children: "child",
    feet: "foot",
    teeth: "tooth",
    geese: "goose",
    men: "man",
    women: "woman",
    mice: "mouse",
    people: "person",
  };

  for (const [plural, singular] of Object.entries(irregulars)) {
    if (word.endsWith(plural) || word === plural) {
      return word.replace(plural, singular);
    }
  }

  // Regular plurals
  if (word.endsWith("ies") && word.length > 4) {
    return word.slice(0, -3) + "y"; // berries -> berry
  }
  if (word.endsWith("ves")) {
    return word.slice(0, -3) + "f"; // leaves -> leaf
  }
  if (
    word.endsWith("ses") ||
    word.endsWith("ches") ||
    word.endsWith("shes") ||
    word.endsWith("xes")
  ) {
    return word.slice(0, -2); // boxes -> box
  }
  if (word.endsWith("s") && !word.endsWith("ss") && !word.endsWith("us")) {
    return word.slice(0, -1); // apples -> apple
  }

  return word;
}

/**
 * Category priority for word-based fallback matching
 * When multiple words match different categories, use this order (higher = more specific)
 */
const CATEGORY_PRIORITY: Record<Category, number> = {
  meat: 5,
  seafood: 5,
  produce: 4,
  fruit: 4,
  dairy: 3,
  other: 1,
};

/**
 * Word-based fallback categorization
 * Splits the text into words and checks if any word matches in the dictionary
 * Used when exact phrase lookup fails (e.g., "salmon head" → finds "salmon")
 * For Chinese: splits into individual characters (e.g., "虱目魚頭" → checks "魚")
 *
 * @param text - Normalized ingredient text
 * @param dictionary - Dictionary to search in
 * @returns Category if any word matches, undefined otherwise
 */
function wordBasedFallback(
  text: string,
  dictionary: Record<string, Category>
): Category | undefined {
  // Check if text contains Chinese characters
  const hasChinese = /[\u4e00-\u9fa5]/.test(text);

  let words: string[];

  if (hasChinese) {
    // Skip fallback for processed/preserved Chinese foods
    // These contain food characters but should remain "other"
    const processedIndicators = [
      // Simplified Chinese
      "油",
      "酱",
      "汁",
      "料",
      "粉",
      "底料",
      "调料",
      "泡",
      "腌",
      "酸",
      "咸",
      "老抽",
      "生抽",
      "辣酱",
      "辣油",
      "火锅",
      "酱油",
      "醋",
      "盐",
      "糖",
      "面",
      "酥",
      "牌",
      "松",
      "荫",
      // Traditional Chinese
      "醬",
      "醬油",
      "鹽",
      "糖",
      "糕",
      "煎",
      "炸",
      "排",
      "丸",
      "板",
      "餃",
      "鹽酥",
      "辣醬",
      "辣椒醬",
      "醋",
      "調味",
      "調料",
      "餅",
      "麵",
      "棒",
      "子",
      "酥",
      "牌",
      "鬆",
      "蔭",
    ];

    if (processedIndicators.some((indicator) => text.includes(indicator))) {
      return undefined; // Don't use fallback for processed foods
    }

    // For Chinese text, split into individual characters
    // Also include 2-character and 3-character combinations for common words
    words = [];
    const chars = text.split("");

    // Add individual characters
    words.push(...chars);

    // Add 2-character combinations
    for (let i = 0; i < chars.length - 1; i++) {
      words.push(chars[i] + chars[i + 1]);
    }

    // Add 3-character combinations (for common food terms)
    for (let i = 0; i < chars.length - 2; i++) {
      words.push(chars[i] + chars[i + 1] + chars[i + 2]);
    }
  } else {
    // For English/other languages, split by common separators (space, dash, slash)
    words = text.split(/[\s\-\/]+/).filter((w) => w.length > 0);
  }

  if (words.length <= 1 && !hasChinese) {
    return undefined; // No compound words to check (for non-Chinese)
  }

  const matchedCategories: Category[] = [];

  for (const word of words) {
    // Check exact word match
    if (dictionary[word]) {
      matchedCategories.push(dictionary[word]);
    } else if (!hasChinese) {
      // Try singularized version (only for English)
      const singular = singularize(word);
      if (singular !== word && dictionary[singular]) {
        matchedCategories.push(dictionary[singular]);
      }
    }
  }

  if (matchedCategories.length === 0) {
    return undefined;
  }

  // If all matches are the same category, return it
  const uniqueCategories = [...new Set(matchedCategories)];
  if (uniqueCategories.length === 1) {
    return uniqueCategories[0];
  }

  // Multiple different categories found - use priority
  // Prefer food categories (meat/seafood/produce/fruit/dairy) over "other"
  const bestCategory = uniqueCategories.reduce((best, current) => {
    return CATEGORY_PRIORITY[current] > CATEGORY_PRIORITY[best] ? current : best;
  });

  return bestCategory;
}

/**
 * Categorize an ingredient using merged multilingual dictionary lookup
 *
 * Language-agnostic approach: Users can type in ANY supported language.
 * No locale detection or restrictions - bilingual users can mix languages freely.
 *
 * @param text - Raw ingredient text from user input (any language)
 * @param locale - Optional locale hint for locale-specific categorization
 *                 If provided, uses locale-specific dictionary (preserves cultural differences)
 *                 If not provided, uses merged dictionary (accepts any language)
 * @returns Category ("meat", "seafood", "produce", "fruit", "dairy", "other")
 *
 * @example
 * categorizeIngredient("chicken")           // "meat" (English)
 * categorizeIngredient("鸡肉")              // "meat" (Simplified Chinese, merged)
 * categorizeIngredient("雞肉")              // "meat" (Traditional Chinese, merged)
 * categorizeIngredient("干貝")              // "seafood" (merged)
 * categorizeIngredient("桂圆", "zh-CN")     // "other" (locale-specific: dried)
 * categorizeIngredient("桂圓", "zh-TW")     // "fruit" (locale-specific: fresh)
 */
export function categorizeIngredient(text: string, locale?: Locale): Category {
  if (!text || text.trim() === "") {
    return "other";
  }

  // Choose dictionary based on locale hint
  // If locale provided: use locale-specific dictionary (preserves cultural differences)
  // If no locale: use merged dictionary (language-agnostic, accepts any language)
  const dictionary = locale && DICTIONARIES[locale] ? DICTIONARIES[locale] : MERGED_DICTIONARY;

  const processingLocale = locale || "en";

  // Step 1: Normalize (lowercase + trim)
  let normalized = text.toLowerCase().trim();

  // Step 2: Try exact lookup FIRST (before any transformations)
  let category = dictionary[normalized];
  if (category) {
    return category;
  }

  // Step 3: Detect shelf-stable containers (can, jar, box) - English only
  // These typically indicate processed/preserved items in the canned goods section
  const { hasShelfStableContainer, baseItem } = detectShelfStableContainer(text);

  if (hasShelfStableContainer && processingLocale === "en") {
    let baseNormalized = baseItem.toLowerCase().trim();
    baseNormalized = singularize(baseNormalized);

    const baseCategory = dictionary[baseNormalized];

    // Fresh food in shelf-stable containers → "other" (processed/canned section)
    if (
      baseCategory === "produce" ||
      baseCategory === "fruit" ||
      baseCategory === "meat" ||
      baseCategory === "seafood" ||
      baseCategory === "dairy"
    ) {
      return "other";
    }

    if (baseCategory === "other") {
      return "other";
    }

    return "other";
  }

  // Step 4: Plural removal (English only)
  if (processingLocale === "en") {
    normalized = singularize(normalized);
  }

  // Try lookup after singularization
  category = dictionary[normalized];
  if (category) {
    return category;
  }

  // Step 5: Quantity stripping
  normalized = removeQuantityPrefix(normalized);

  // Re-singularize after stripping (English only)
  if (processingLocale === "en") {
    normalized = singularize(normalized);
  }

  normalized = normalized.toLowerCase().trim();

  // Step 6: Final lookup after all normalization
  category = dictionary[normalized];

  // Step 7: Word-based fallback - works for ALL languages
  if (!category) {
    const fallbackCategory = wordBasedFallback(normalized, dictionary);
    if (fallbackCategory) {
      category = fallbackCategory;
    }
  }

  // Step 8: Fallback
  return category || "other";
}
